// showall.js — the "Show All" panel: sequential queue (reorderable), open
// tasks (selectable), completed tasks; each row deletable.

import { S, labelOf } from './state.js';
import { rememberDeleted } from '../model.js';
import { setActiveTask, dropActiveTask, updateLockState } from './taskbox.js';
import { saveAll } from './sync.js';
import { updateStats } from './statsbox.js';

const editAllBtn = document.getElementById('toggleAll');
const PANEL_ID = 'readonlyCompleted';

export function isShowAllOpen() { return document.getElementById(PANEL_ID) !== null; }

export function closeShowAll() {
  document.getElementById(PANEL_ID)?.remove();
  document.body.classList.remove('list-open'); // the floating photo may show again
  editAllBtn.textContent = 'Show All';
  editAllBtn.setAttribute('aria-expanded', 'false');
  updateLockState();
}

// Re-render if open (after a change made elsewhere), keeping focus sensible.
export function refreshShowAll(focusSelector) {
  if (!isShowAllOpen()) return;
  document.getElementById(PANEL_ID)?.remove();
  openShowAll();
  if (focusSelector) document.querySelector(focusSelector)?.focus();
}

export function toggleShowAll() {
  if (isShowAllOpen()) { closeShowAll(); saveAll(); updateStats(); }
  else openShowAll();
}

function el(tag, className, text) {
  const e = document.createElement(tag);
  if (className) e.className = className;
  if (text !== undefined) e.textContent = text;
  return e;
}
function iconButton(className, symbol, label, onClick) {
  const b = el('button', className, symbol);
  b.type = 'button';
  b.title = label;
  b.setAttribute('aria-label', label);
  b.addEventListener('click', onClick);
  return b;
}

function openShowAll() {
  const container = el('section', 'task-lists');
  container.id = PANEL_ID;
  container.setAttribute('aria-label', 'All tasks');

  const list = (label) => { const ul = el('ul', 'task-list'); ul.setAttribute('aria-label', label); return ul; };
  const row = (text) => { const li = el('li', 'task-row'); const span = el('span', 'task-row__text', text); return { li, span }; };
  const deleteBtn = (t, onDelete) => iconButton('task-row__delete', '×', `Delete: ${t.text}`, onDelete);

  // --- Sequential queue (Randomize OFF) first ---
  if (S.manualQueue.length > 0) {
    container.appendChild(el('h2', 'task-lists__header', 'Blue tasks come in order (Randomize: OFF)'));
    const ul = list('Sequential tasks');
    const move = async (from, to) => {
      if (to < 0 || to >= S.manualQueue.length || from === to) return;
      const [item] = S.manualQueue.splice(from, 1);
      S.manualQueue.splice(to, 0, item);
      await saveAll();
      refreshShowAll(`[data-queue-index="${to}"] .task-row__move--${to < from ? 'up' : 'down'}`);
    };
    S.manualQueue.forEach((t, i) => {
      const { li, span } = row(labelOf(t));
      li.classList.add('task-row--queued'); // the colour marker is drawn in CSS, not typed as an emoji
      li.dataset.queueIndex = i;
      li.dataset.index = i;
      // Mouse: drag and drop
      li.draggable = true;
      li.addEventListener('dragstart', (e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', i); li.classList.add('dragging'); });
      li.addEventListener('dragend', () => li.classList.remove('dragging'));
      li.addEventListener('dragover', (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; });
      li.addEventListener('drop', (e) => { e.preventDefault(); move(Number(e.dataTransfer.getData('text/plain')), Number(li.dataset.index)); });
      // Keyboard / touch: move up & down
      const moves = el('span', 'task-row__moves');
      const up = iconButton('task-row__move task-row__move--up', '↑', `Move up: ${t.text}`, () => move(i, i - 1));
      const down = iconButton('task-row__move task-row__move--down', '↓', `Move down: ${t.text}`, () => move(i, i + 1));
      up.disabled = i === 0; down.disabled = i === S.manualQueue.length - 1;
      moves.appendChild(up); moves.appendChild(down);
      li.appendChild(moves);
      li.appendChild(span);
      li.appendChild(deleteBtn(t, async () => {
        S.manualQueue = S.manualQueue.filter(x => x.id !== t.id);
        S.deletedIds = rememberDeleted(S.deletedIds, t.id);
        await saveAll();
        refreshShowAll();
        updateStats();
      }));
      ul.appendChild(li);
    });
    container.appendChild(ul);
    container.appendChild(el('hr', 'task-lists__sep'));
  }

  // --- Open tasks (random pool), sorted by tag then text ---
  if (S.manualQueue.length > 0) container.appendChild(el('h2', 'task-lists__header', 'Pink tasks are random (Randomize: ON)'));
  const openList = list('Open tasks');
  S.tasks
    .slice()
    .sort((a, b) => {
      const tagA = a.tag?.toLowerCase() || '';
      const tagB = b.tag?.toLowerCase() || '';
      if (tagA !== tagB) return tagA.localeCompare(tagB);
      return a.text.localeCompare(b.text);
    })
    .forEach((t) => {
      const { li, span } = row(labelOf(t));
      li.appendChild(iconButton('task-row__select', '', `Work on: ${t.text}`, () => {
        setActiveTask(t);
        closeShowAll();
        saveAll();
      }));
      li.appendChild(span);
      li.appendChild(deleteBtn(t, async () => {
        S.tasks = S.tasks.filter(x => x.id !== t.id);
        S.deletedIds = rememberDeleted(S.deletedIds, t.id);
        if (t.id === S.currentFishId) dropActiveTask();
        await saveAll();
        refreshShowAll();
        updateStats();
      }));
      openList.appendChild(li);
    });
  container.appendChild(openList);
  container.appendChild(el('hr', 'task-lists__sep'));

  // --- Completed, in order of completion ---
  if (S.manualQueue.length > 0) container.appendChild(el('h2', 'task-lists__header', 'Green tasks are done'));
  if (S.historyWindowDays && S.completed.length > 0) {
    container.appendChild(el('p', 'task-lists__note',
      `Showing the last ${S.historyWindowDays} days of completed tasks. The stats page and your data export include everything.`));
  }
  const doneList = list('Completed tasks');
  S.completed.forEach((t) => {
    const { li, span } = row(`${labelOf(t)} ${t.duration || ''}`);
    li.classList.add('task-row--done');
    li.appendChild(span);
    li.appendChild(deleteBtn(t, async () => {
      S.completed = S.completed.filter(x => x.id !== t.id);
      S.deletedIds = rememberDeleted(S.deletedIds, t.id);
      await saveAll();
      refreshShowAll();
      updateStats();
    }));
    doneList.appendChild(li);
  });
  container.appendChild(doneList);

  // The list goes below the add box and the options row, so opening it never
  // moves the controls (listSlot); older markup falls back to after the buttons.
  const slot = document.getElementById('listSlot');
  if (slot) slot.appendChild(container);
  else document.getElementById('buttonRow').insertAdjacentElement('afterend', container);
  document.body.classList.add('list-open'); // hides the floating photo on phones
  editAllBtn.textContent = 'Hide';
  editAllBtn.setAttribute('aria-expanded', 'true');
  updateLockState();
}

export function initShowAll() {
  editAllBtn.addEventListener('click', toggleShowAll);
}
